import apiClient from './apiClient';
import { saveOffline, getOfflineData, deleteOfflineData, STORES } from '../utils/offlineDB';

/**
 * Prüft ob der Browser online ist
 */
const isOnline = () => navigator.onLine;

/**
 * Lädt alle Daten für den Arbeitsnachweis (Vorschau)
 */
export const getArbeitsnachweisData = async (anfrageId) => {
  const response = await apiClient.get(`/arbeitsnachweise/anfrage/${anfrageId}/daten`);
  return response.data;
};

/**
 * Generiert ein Arbeitsnachweis-PDF
 * Im Offline-Modus wird der Arbeitsnachweis lokal gespeichert und später synchronisiert
 */
export const generateArbeitsnachweis = async (anfrageId, options = {}, offlineData = null) => {
  // Online: Direkt an Server senden
  if (isOnline()) {
    const response = await apiClient.post(`/arbeitsnachweise/anfrage/${anfrageId}/generieren`, options);
    return response.data;
  }

  // Offline: Lokal speichern für spätere Synchronisation
  if (!offlineData) {
    throw new Error('Offline-Modus erfordert offlineData Parameter');
  }

  const pendingArbeitsnachweis = {
    anfrageId,
    options,
    kunde: offlineData.kunde,
    anfrage: offlineData.anfrage,
    timestamp: Date.now(),
    status: 'pending'
  };

  const offlineId = await saveOffline(STORES.ARBEITSNACHWEISE, pendingArbeitsnachweis);
  console.log('📴 Arbeitsnachweis offline gespeichert:', offlineId);

  return {
    data: {
      success: true,
      offline: true,
      offlineId,
      message: 'Arbeitsnachweis wird erstellt, sobald Sie wieder online sind.',
      nummer: `OFFLINE-${offlineId}`
    }
  };
};

/**
 * Holt alle ausstehenden Offline-Arbeitsnachweise
 */
export const getPendingArbeitsnachweise = async () => {
  return await getOfflineData(STORES.ARBEITSNACHWEISE);
};

/**
 * Synchronisiert einen einzelnen Offline-Arbeitsnachweis
 */
export const syncArbeitsnachweis = async (pendingItem) => {
  try {
    const { anfrageId, options } = pendingItem.data;
    const response = await apiClient.post(`/arbeitsnachweise/anfrage/${anfrageId}/generieren`, options);

    // Erfolgreich synchronisiert - aus IndexedDB löschen
    await deleteOfflineData(STORES.ARBEITSNACHWEISE, pendingItem.id);
    console.log('✅ Arbeitsnachweis synchronisiert:', response.data);

    return {
      success: true,
      data: response.data,
      pendingItem
    };
  } catch (error) {
    console.error('❌ Fehler beim Synchronisieren:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      pendingItem
    };
  }
};

/**
 * Synchronisiert alle ausstehenden Offline-Arbeitsnachweise
 */
export const syncAllPendingArbeitsnachweise = async () => {
  const pendingItems = await getPendingArbeitsnachweise();

  if (pendingItems.length === 0) {
    return { synced: 0, failed: 0, results: [] };
  }

  console.log(`🔄 Synchronisiere ${pendingItems.length} Arbeitsnachweise...`);

  const results = [];
  let synced = 0;
  let failed = 0;

  for (const item of pendingItems) {
    const result = await syncArbeitsnachweis(item);
    results.push(result);

    if (result.success) {
      synced++;
    } else {
      failed++;
    }
  }

  console.log(`✅ Synchronisation abgeschlossen: ${synced} erfolgreich, ${failed} fehlgeschlagen`);

  return { synced, failed, results };
};

/**
 * Sendet einen Arbeitsnachweis per E-Mail
 */
export const sendArbeitsnachweis = async (anfrageId, data) => {
  const response = await apiClient.post(`/arbeitsnachweise/anfrage/${anfrageId}/senden`, data);
  return response.data;
};

/**
 * Lädt die Liste der Arbeitsnachweise einer Anfrage
 */
export const getArbeitsnachweiseFuerAnfrage = async (anfrageId) => {
  const response = await apiClient.get(`/arbeitsnachweise/anfrage/${anfrageId}/liste`);
  return response.data;
};

/**
 * Lädt die Liste der Arbeitsnachweise eines Kunden
 */
export const getArbeitsnachweiseFuerKunde = async (kundeId) => {
  const response = await apiClient.get(`/arbeitsnachweise/kunde/${kundeId}`);
  return response.data;
};

export default {
  getArbeitsnachweisData,
  generateArbeitsnachweis,
  sendArbeitsnachweis,
  getArbeitsnachweiseFuerAnfrage,
  getArbeitsnachweiseFuerKunde,
  getPendingArbeitsnachweise,
  syncArbeitsnachweis,
  syncAllPendingArbeitsnachweise
};
